// ApiAccessService.js

import { getAuth } from 'firebase/auth';

function cleanString(value) {
    return String(value || '').trim();
}

function resolveWorkspaceApiBase() {
    const configured = cleanString(process.env.REACT_APP_WORKSPACE_API_BASE_URL).replace(/\/+$/, '');
    if (configured) return configured;

    return '';
}

function getWorkspaceBasePath(workspaceId) {
    const cleanWorkspaceId = cleanString(workspaceId);
    if (!cleanWorkspaceId) {
        throw new Error('workspaceId is required.');
    }

    const encodedWorkspaceId = encodeURIComponent(cleanWorkspaceId);
    const path = `/v1/workspaces/${encodedWorkspaceId}`;
    const workspaceApiBase = resolveWorkspaceApiBase();
    if (!workspaceApiBase) return path;
    return `${workspaceApiBase}${path}`;
}

function parseJson(text) {
    if (!text) return null;
    try {
        return JSON.parse(text);
    } catch (_error) {
        return null;
    }
}

function resolveApiErrorMessage(payload, fallbackMessage) {
    if (payload && typeof payload.error === 'object' && typeof payload.error.message === 'string') {
        const message = cleanString(payload.error.message);
        if (message) return message;
    }

    if (payload && typeof payload.message === 'string') {
        const message = cleanString(payload.message);
        if (message) return message;
    }

    return fallbackMessage;
}

function buildApiError(status, payload, fallbackMessage) {
    const error = new Error(resolveApiErrorMessage(payload, fallbackMessage));
    error.status = status;
    error.payload = payload;
    return error;
}

async function resolveBearerToken(overrideToken) {
    const explicitToken = cleanString(overrideToken);
    if (explicitToken) return explicitToken;

    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
        throw new Error('You must be signed in to call the workspace API.');
    }

    return user.getIdToken();
}

async function requestWorkspaceApi({
    workspaceId,
    path,
    method = 'GET',
    body,
    bearerToken,
    headers = {},
    includeMeta = false,
}) {
    const cleanPath = cleanString(path);
    if (!cleanPath || !cleanPath.startsWith('/')) {
        throw new Error('Workspace API path must start with "/".');
    }

    const token = await resolveBearerToken(bearerToken);
    const url = `${getWorkspaceBasePath(workspaceId)}${cleanPath}`;

    let response;
    try {
        response = await fetch(url, {
            method,
            headers: {
                Authorization: `Bearer ${token}`,
                ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
                ...headers,
            },
            body: body === undefined ? undefined : JSON.stringify(body),
        });
    } catch (networkError) {
        const message = cleanString(networkError?.message) || 'Failed to fetch';
        throw new Error(
            `Network error calling Workspace API at ${url}: ${message}. `
            + 'Set REACT_APP_WORKSPACE_API_BASE_URL to your deployed API origin, or run the local API runtime.',
        );
    }

    const text = await response.text();
    const payload = parseJson(text);

    if (!response.ok) {
        throw buildApiError(response.status, payload, `Workspace API request failed (${response.status}).`);
    }

    if (!includeMeta && payload === null && cleanString(text)) {
        const looksLikeHtml = /^\s*</.test(text);
        const fallbackMessage = looksLikeHtml
            ? 'Workspace API returned HTML instead of JSON. Check REACT_APP_WORKSPACE_API_BASE_URL and backend routing.'
            : 'Workspace API returned a non-JSON response.';

        throw buildApiError(response.status, payload, fallbackMessage);
    }

    if (includeMeta) {
        return {
            status: response.status,
            headers: Object.fromEntries(response.headers.entries()),
            body: payload,
            text,
        };
    }

    return payload;
}

export async function listWorkspaceServiceAccounts({ workspaceId }) {
    return requestWorkspaceApi({
        workspaceId,
        path: '/service-accounts',
        method: 'GET',
    });
}

export async function createWorkspaceType({
    workspaceId,
    label,
    labelPlural,
    description,
    iconKey,
    defaultView,
    schema,
}) {
    return requestWorkspaceApi({
        workspaceId,
        path: '/types',
        method: 'POST',
        body: {
            label,
            labelPlural,
            description,
            iconKey,
            defaultView,
            schema,
        },
    });
}

export async function listWorkspaceWebhookRules({ workspaceId }) {
    return requestWorkspaceApi({
        workspaceId,
        path: '/webhooks/rules',
        method: 'GET',
    });
}

export async function createWorkspaceWebhookRule({
    workspaceId,
    name,
    typeId,
    event = 'node.created',
    webhookId,
    destinationUrl,
    destinationName,
}) {
    return requestWorkspaceApi({
        workspaceId,
        path: '/webhooks/rules',
        method: 'POST',
        body: {
            name,
            typeId,
            event,
            webhookId,
            destinationUrl,
            destinationName,
        },
    });
}

export async function updateWorkspaceWebhookRule({ workspaceId, ruleId, patch }) {
    const id = cleanString(ruleId);
    if (!id) throw new Error('ruleId is required.');

    return requestWorkspaceApi({
        workspaceId,
        path: `/webhooks/rules/${encodeURIComponent(id)}`,
        method: 'PATCH',
        body: patch,
    });
}

export async function updateWorkspaceWebhookEndpoint({ workspaceId, webhookId, patch }) {
    const id = cleanString(webhookId);
    if (!id) throw new Error('webhookId is required.');

    return requestWorkspaceApi({
        workspaceId,
        path: `/webhooks/endpoints/${encodeURIComponent(id)}`,
        method: 'PATCH',
        body: patch,
    });
}

export async function deleteWorkspaceWebhookRule({ workspaceId, ruleId }) {
    const id = cleanString(ruleId);
    if (!id) throw new Error('ruleId is required.');

    return requestWorkspaceApi({
        workspaceId,
        path: `/webhooks/rules/${encodeURIComponent(id)}`,
        method: 'DELETE',
    });
}

export async function testWorkspaceWebhookRule({ workspaceId, ruleId, payload }) {
    const id = cleanString(ruleId);
    if (!id) throw new Error('ruleId is required.');

    return requestWorkspaceApi({
        workspaceId,
        path: `/webhooks/rules/${encodeURIComponent(id)}/test`,
        method: 'POST',
        body: payload === undefined ? {} : { payload },
    });
}

export async function listWorkspaceIncomingHooks({ workspaceId }) {
    return requestWorkspaceApi({
        workspaceId,
        path: '/incoming/hooks',
        method: 'GET',
    });
}

export async function createWorkspaceIncomingHook({
    workspaceId,
    name,
    typeId,
    action = 'create',
}) {
    return requestWorkspaceApi({
        workspaceId,
        path: '/incoming/hooks',
        method: 'POST',
        body: {
            name,
            typeId,
            action,
        },
    });
}

export async function updateWorkspaceIncomingHook({ workspaceId, hookId, patch }) {
    const id = cleanString(hookId);
    if (!id) throw new Error('hookId is required.');

    return requestWorkspaceApi({
        workspaceId,
        path: `/incoming/hooks/${encodeURIComponent(id)}`,
        method: 'PATCH',
        body: patch,
    });
}

export async function deleteWorkspaceIncomingHook({ workspaceId, hookId }) {
    const id = cleanString(hookId);
    if (!id) throw new Error('hookId is required.');

    return requestWorkspaceApi({
        workspaceId,
        path: `/incoming/hooks/${encodeURIComponent(id)}`,
        method: 'DELETE',
    });
}

export async function createWorkspaceServiceAccount({ workspaceId, name, role, scopes, permissions }) {
    return requestWorkspaceApi({
        workspaceId,
        path: '/service-accounts',
        method: 'POST',
        body: {
            name,
            role,
            scopes,
            permissions,
        },
    });
}

export async function rotateWorkspaceServiceAccountToken({ workspaceId, serviceAccountId }) {
    const id = cleanString(serviceAccountId);
    if (!id) throw new Error('serviceAccountId is required.');

    return requestWorkspaceApi({
        workspaceId,
        path: `/service-accounts/${encodeURIComponent(id)}/rotate-token`,
        method: 'POST',
    });
}

export async function revokeWorkspaceServiceAccount({ workspaceId, serviceAccountId }) {
    const id = cleanString(serviceAccountId);
    if (!id) throw new Error('serviceAccountId is required.');

    return requestWorkspaceApi({
        workspaceId,
        path: `/service-accounts/${encodeURIComponent(id)}/revoke`,
        method: 'POST',
    });
}

export async function deleteWorkspaceServiceAccount({ workspaceId, serviceAccountId }) {
    const id = cleanString(serviceAccountId);
    if (!id) throw new Error('serviceAccountId is required.');

    try {
        return await requestWorkspaceApi({
            workspaceId,
            path: `/service-accounts/${encodeURIComponent(id)}`,
            method: 'DELETE',
        });
    } catch (error) {
        const status = Number(error?.status || 0);
        const message = cleanString(error?.message).toLowerCase();
        const shouldFallback = status === 404 && message.includes('route not found');

        if (!shouldFallback) {
            throw error;
        }

        return requestWorkspaceApi({
            workspaceId,
            path: `/service-accounts/${encodeURIComponent(id)}/delete`,
            method: 'POST',
        });
    }
}

export async function updateWorkspaceServiceAccountConnectionPolicy({
    workspaceId,
    serviceAccountId,
    enabled,
    readRpm,
    writeRpm,
    dataVisibility,
    notes,
    targetSystem,
    templateId,
}) {
    const id = cleanString(serviceAccountId);
    if (!id) throw new Error('serviceAccountId is required.');

    return requestWorkspaceApi({
        workspaceId,
        path: `/service-accounts/${encodeURIComponent(id)}/connection-policy`,
        method: 'POST',
        body: {
            enabled,
            readRpm,
            writeRpm,
            dataVisibility,
            notes,
            targetSystem,
            templateId,
        },
    });
}

export async function getWorkspaceApiLimits({ workspaceId }) {
    return requestWorkspaceApi({
        workspaceId,
        path: '/limits',
        method: 'GET',
    });
}

export async function getWorkspaceOpenApiSpec({ workspaceId }) {
    return requestWorkspaceApi({
        workspaceId,
        path: '/openapi.json',
        method: 'GET',
    });
}

export async function getWorkspaceSchemaDoc({ workspaceId }) {
    return requestWorkspaceApi({
        workspaceId,
        path: '/schema.json',
        method: 'GET',
    });
}

export async function runApiTestRequest({
    workspaceId,
    bearerToken,
    path,
    method = 'GET',
    body,
}) {
    const normalizedMethod = cleanString(method || 'GET').toUpperCase();
    const withBody = ['POST', 'PATCH', 'PUT'].includes(normalizedMethod);

    return requestWorkspaceApi({
        workspaceId,
        path,
        method: normalizedMethod,
        body: withBody ? body : undefined,
        bearerToken,
        includeMeta: true,
    });
}
